import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client';
import { Alert, Note } from '../ui';
import { AuthPage, Label } from './auth-parts';

/** NF-01 — the same rule the API enforces (`MIN_PASSWORD_LENGTH`, auth/account-recovery.ts). */
const MIN_PASSWORD_LENGTH = 12;

type Step = 'ask' | 'reset' | 'done';

/**
 * P-04 — Forgot password (ON-10). Pre-authentication, archetype 1, rendered in
 * place of Sign In the way Apply is.
 *
 * Two steps on one screen, because the code arrives by email and the person comes
 * back to this same tab to type it: ask for a code, then type the code with the new
 * password. Nothing about the account changes until the second step succeeds.
 *
 * The first step says the same thing whatever address is typed. The API cannot be
 * told apart either (account-recovery.ts, FORGOT_RESPONSE), and the screen must not
 * undo that by, say, only showing step 2 for a known address. It always moves on.
 */
export function ForgotPassword({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('ask');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function ask(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post('/auth/forgot', { email });
      setStep('reset');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('auth.reset.failed'));
    } finally {
      setBusy(false);
    }
  }

  async function reset(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t('auth.changePassword.tooShort'));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.changePassword.mismatch'));
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/reset', { code, newPassword: password });
      setStep('done');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('auth.reset.failed'));
    } finally {
      setBusy(false);
    }
  }

  const back = (
    <>
      <hr className="mk-auth-rule" />
      <div className="mk-auth-foot">
        <button type="button" className="mk-auth-alt" onClick={onBack}>{t('auth.backToSignIn')}</button>
      </div>
    </>
  );

  if (step === 'done') {
    return (
      <AuthPage eyebrow={t('auth.eyebrow')} title={t('auth.reset.doneTitle')} lede={t('auth.reset.doneBody')}>
        <button className="mk-auth-submit" type="button" onClick={onBack}>{t('auth.reset.signInNow')}</button>
      </AuthPage>
    );
  }

  if (step === 'reset') {
    return (
      <AuthPage
        eyebrow={t('auth.eyebrow')}
        title={t('auth.reset.codeTitle')}
        lede={t('auth.reset.codeBody', { email })}
      >
        <form onSubmit={(e) => void reset(e)} noValidate>
          {error ? <Alert tone="stop">{error}</Alert> : null}

          <div className="mk-auth-field">
            <Label htmlFor="resetCode" required>{t('auth.reset.code')}</Label>
            <input
              id="resetCode" required autoComplete="one-time-code" autoCapitalize="characters"
              spellCheck={false} maxLength={12}
              value={code} onChange={(e) => setCode(e.target.value)}
            />
          </div>

          <div className="mk-auth-field">
            <Label htmlFor="resetNew" required>{t('auth.changePassword.new')}</Label>
            <input
              id="resetNew" type="password" required autoComplete="new-password" minLength={MIN_PASSWORD_LENGTH}
              value={password} onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div className="mk-auth-field">
            <Label htmlFor="resetConfirm" required>{t('auth.changePassword.confirm')}</Label>
            <input
              id="resetConfirm" type="password" required autoComplete="new-password" minLength={MIN_PASSWORD_LENGTH}
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
            />
          </div>

          <button className="mk-auth-submit" type="submit" disabled={busy || !code.trim()}>
            {busy ? t('auth.submitting') : t('auth.reset.submit')}
          </button>
        </form>
        <Note style={{ marginTop: 'var(--mk-space-4)' }}>
          {t('auth.reset.noCode')}{' '}
          <button type="button" className="mk-auth-link" onClick={() => { setStep('ask'); setCode(''); setError(null); }}>
            {t('auth.reset.askAgain')}
          </button>
        </Note>
        {back}
      </AuthPage>
    );
  }

  return (
    <AuthPage eyebrow={t('auth.eyebrow')} title={t('auth.reset.title')} lede={t('auth.reset.body')}>
      <form onSubmit={(e) => void ask(e)} noValidate>
        {error ? <Alert tone="stop">{error}</Alert> : null}
        <div className="mk-auth-field">
          <Label htmlFor="resetEmail" required>{t('auth.email')}</Label>
          <input
            id="resetEmail" type="email" required autoComplete="username"
            placeholder={t('auth.emailPlaceholder')}
            value={email} onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <button className="mk-auth-submit" type="submit" disabled={busy || !email.trim()}>
          {busy ? t('auth.submitting') : t('auth.reset.send')}
        </button>
      </form>
      {back}
    </AuthPage>
  );
}
